import { __awaiter } from "tslib";
import { ZoneService, } from '@citrite/sf-api';
import { ProjectsNetworkError } from '../ProjectsNetworkError';
import { ResourceEntityType, SystemIdentifier, } from '../types';
const configuration = {
    origin: window.origin,
    apiPath: '/io/projects/',
};
export function configureHttp(configure) {
    configure(configuration);
}
function createRequest(path, init) {
    const url = `${configuration.origin}${configuration.apiPath}${path}`;
    init.headers = Object.assign(Object.assign({}, init.headers), { 'X-BFF-CSRF': 'true' });
    return new Request(url, init);
}
export const DefaultProjectDataProvider = {
    createProject(project) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest('projects', {
                method: 'POST',
                body: JSON.stringify(project),
                headers: {
                    'content-type': 'application/json',
                },
            });
            const response = yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 201,
                defaultErrorMessage: 'Unable to create project',
            });
            const createdProject = yield response.json();
            return createdProject;
        });
    },
    getSortedProjectsWithEntitlements(containerRID, sortField, sortDirection, status, lifeCycleStages) {
        return __awaiter(this, void 0, void 0, function* () {
            const params = new URLSearchParams({
                'sort.field': sortField,
                'sort.direction': sortDirection,
                containerRID,
            });
            if (status) {
                params.append('status', status);
            }
            lifeCycleStages === null || lifeCycleStages === void 0 ? void 0 : lifeCycleStages.forEach(l => params.append('state', l));
            const request = createRequest(`projects/withentitlements?${params.toString()}`, {
                method: 'GET',
            });
            const response = yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 200,
                defaultErrorMessage: 'Unable to get projects with entitlements',
            });
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
    getSortedProjectsByContainerRID(containerRID, sortField, sortDirection) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects?containerRID=${containerRID}&sort.field=${sortField}&sort.direction=${sortDirection}`, { method: 'GET' });
            const response = yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 200,
                defaultErrorMessage: 'Unable to get projects',
            });
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
    getDeletedProjects(containerRID) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects?state=Deleted&containerRID=${containerRID}`, {
                method: 'GET',
            });
            const response = yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 200,
                defaultErrorMessage: 'Unable to get deleted projects',
            });
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
    getProjectById(projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}`, { method: 'GET' });
            const response = yield fetch(request);
            if (response.status !== 200) {
                throw new ProjectsNetworkError('Unable to get projects', response);
            }
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
    putProject(project) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${project.id}`, {
                method: 'PUT',
                body: JSON.stringify(project),
                headers: {
                    'content-type': 'application/json',
                },
            });
            const response = yield fetch(request);
            if (response.status !== 200) {
                throw new ProjectsNetworkError('Unable to put project', response);
            }
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
    deleteProject(projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}`, {
                method: 'DELETE',
            });
            yield fetchOrThrowErrorFromResponse(request);
        });
    },
    getProjectUsers(projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = yield createRequest(`projects/${projectId}/users`, {
                method: 'GET',
            });
            const response = yield fetch(request);
            if (response.status !== 200) {
                throw new ProjectsNetworkError('could not get project users', response);
            }
            const body = yield response.json();
            return body;
        });
    },
    deleteProjectUser(userId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/users/${userId}`, {
                method: 'DELETE',
            });
            const response = yield fetch(request);
            if (response.status !== 204) {
                throw new ProjectsNetworkError('Unable to remove user', response);
            }
        });
    },
    addUserToProject(projectId, payload) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/users`, {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: {
                    'content-type': 'application/json',
                },
            });
            const response = yield fetch(request);
            if (response.status !== 204) {
                throw new ProjectsNetworkError('Unable to add user to project', response);
            }
        });
    },
    addDelegatingClient(projectId, payload) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/users/delegate`, {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: {
                    'content-type': 'application/json',
                },
            });
            const response = yield fetch(request);
            if (response.status !== 201) {
                throw new ProjectsNetworkError('Unable to add new client to project', response);
            }
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
    restoreProjectById(projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/restore`, {
                method: 'POST',
                body: JSON.stringify({}),
            });
            yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 204,
                defaultErrorMessage: 'Unable to restore projects',
            });
        });
    },
    getProjectFiles(projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/files`, { method: 'GET' });
            const response = yield fetch(request);
            if (response.status !== 200) {
                throw new ProjectsNetworkError('Unable to get project files', response);
            }
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
    getDeletedProjectFiles(projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/files?state=Deleted`, {
                method: 'GET',
            });
            const response = yield fetch(request);
            if (response.status !== 200) {
                throw new ProjectsNetworkError('Unable to get deleted project files', response);
            }
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
    deleteProjectFiles(projectId, fileIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/files/delete`, {
                method: 'DELETE',
                body: JSON.stringify(fileIds),
                headers: {
                    'content-type': 'application/json',
                },
            });
            yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 204,
                defaultErrorMessage: 'Unexpected status code in response from delete files request',
            });
        });
    },
    restoreProjectFiles(projectId, fileIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/files/restore`, {
                method: 'POST',
                body: JSON.stringify(fileIds),
                headers: {
                    'content-type': 'application/json',
                },
            });
            yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 204,
                defaultErrorMessage: 'Unexpected status code in response from delete files request',
            });
        });
    },
    getProjectFilesBulkDownloadUrl(projectId, fileIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/files/download`, {
                method: 'POST',
                body: JSON.stringify(fileIds),
                headers: {
                    'content-type': 'application/json',
                },
            });
            const response = yield fetch(request);
            if (response.status !== 200) {
                throw new ProjectsNetworkError('Unable to download project files', response);
            }
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
    getRoleByUser(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`sharefileusers/${userId}/role`, {
                method: 'GET',
            });
            const response = yield fetch(request);
            if (response.status !== 200) {
                throw new ProjectsNetworkError('Unable to get user role', response);
            }
            const text = yield response.text();
            return JSON.parse(text);
        });
    },
    uploadFile(projectId, requestingResourceId, uploadRequestParams) {
        return __awaiter(this, void 0, void 0, function* () {
            const searchParams = new URLSearchParams();
            searchParams.append('fileContainerResourceId', requestingResourceId);
            const request = createRequest(`projects/${projectId}/files/upload?${searchParams.toString()}`, {
                method: 'POST',
                body: JSON.stringify(uploadRequestParams),
                headers: {
                    'content-type': 'application/json',
                },
            });
            const response = yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 200,
                defaultErrorMessage: 'Unexpected status code encounter while starting upload',
            });
            const text = yield response.text();
            const uploadSpec = JSON.parse(text, dateReviver);
            return uploadSpec;
        });
    },
    uploadFileComplete(projectId, requestingResourceId, uploadInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            const query = new URLSearchParams();
            query.append('fileSource', requestingResourceId);
            query.append('fileContainerResourceId', requestingResourceId);
            const request = createRequest(`projects/${projectId}/files/uploadcomplete?${query}`, {
                method: 'POST',
                body: JSON.stringify(uploadInfo),
                headers: {
                    'content-type': 'application/json',
                },
            });
            const response = yield fetch(request);
            if (response.status !== 201) {
                throw new ProjectsNetworkError('Unable to complete file upload', response);
            }
            const text = yield response.text();
            const file = JSON.parse(text, dateReviver);
            return file;
        });
    },
    createClient(client) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest('sharefileusers/client', {
                method: 'POST',
                body: JSON.stringify(client),
                headers: {
                    'content-type': 'application/json',
                },
            });
            const response = yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 201,
                defaultErrorMessage: 'Unable to create client',
            });
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
    getProjectZoneServices(projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/zoneservice`, { method: 'GET' });
            const response = yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 200,
                defaultErrorMessage: 'Unable to get project zone services',
            });
            const text = yield response.text();
            return convertZoneServicestoEnum(text.replace(/^"(.*)"$/, '$1'));
        });
    },
    getProjectContacts(rid) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/contacts?resourceId=${rid}`, {
                method: 'GET',
            });
            const response = yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 200,
                defaultErrorMessage: 'Unable to get project contacts',
            });
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
    freezeProject(projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/freeze`, {
                method: 'POST',
            });
            yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 204,
                defaultErrorMessage: 'Unable to freeze project',
            });
        });
    },
    unfreezeProject(projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/${projectId}/unfreeze`, {
                method: 'POST',
            });
            yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 204,
                defaultErrorMessage: 'Unable to unfreeze project',
            });
        });
    },
    queryProjectsByTags(payload, containerRIDPrefix, sortField, sortDirection) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = createRequest(`projects/querybytagswithentitlements?sort.field=${sortField}&sort.direction=${sortDirection}&containerRIDPrefix=${containerRIDPrefix}`, {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: {
                    'content-type': 'application/json',
                },
            });
            const response = yield fetchOrThrowErrorFromResponse(request, {
                expectedStatus: 200,
                defaultErrorMessage: 'Unable to query projects by tags',
            });
            const text = yield response.text();
            return JSON.parse(text, dateReviver);
        });
    },
};
function fetchOrThrowErrorFromResponse(request, config = {}) {
    var _a, _b, _c, _d;
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield fetch(request);
        const isError = config.expectedStatus !== undefined
            ? response.status !== config.expectedStatus
            : response.status > 399;
        if (isError) {
            let errorBody = {};
            if ((_a = response.headers.get('content-type')) === null || _a === void 0 ? void 0 : _a.includes('application/json')) {
                errorBody = yield response.json().catch(_ => ({}));
            }
            const message = (_d = (_c = (_b = errorBody === null || errorBody === void 0 ? void 0 : errorBody.message) !== null && _b !== void 0 ? _b : errorBody === null || errorBody === void 0 ? void 0 : errorBody.Message) !== null && _c !== void 0 ? _c : config.defaultErrorMessage) !== null && _d !== void 0 ? _d : 'Unknown error';
            throw new ProjectsNetworkError(message, response, errorBody === null || errorBody === void 0 ? void 0 : errorBody.statusCode, errorBody);
        }
        return response;
    });
}
function dateReviver(_key, value) {
    if (typeof value === 'string') {
        if (/^\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)$/.test(value)) {
            return new Date(value);
        }
    }
    return value;
}
function convertZoneServicestoEnum(zoneServices) {
    return zoneServices
        .split(', ')
        .map(zs => ZoneService[zs])
        .reduce((acc, item) => acc | item);
}
export function getNonSolutionProjectContainerRIDPrefix(accountId) {
    return `${SystemIdentifier}:${ResourceEntityType.account}-${accountId}/${ResourceEntityType.project}`;
}
//# sourceMappingURL=DefaultProjectDataProvider.js.map